<template>
  <div>
    <dl
      v-for="(item, i) in displayItems"
      :key="i"
      class="boss-resumecard-any-list"
    >
      <dt class="boss-resumecard-any-head">
        {{ item.name }}
      </dt>
      <dd
        v-if="item.hope"
        :data-test="`member-${item.key}`"
        class="boss-resumecard-any-body"
      >
        {{ item.data() | parseHopeLabel(item.name) }}
      </dd>
      <dd
        v-else
        :data-test="`member-${item.key}`"
        class="boss-resumecard-any-body"
      >
        {{ item.data() | parseLabel(item.name) }}
      </dd>
    </dl>
  </div>
</template>

<script>
import { parseHierarchicalObjects } from '@/lib/parse'
import { mapGetters } from 'vuex'
import { createDate } from '@/lib/dateUtils.js'
import _ from 'lodash'
import * as parse from '@/lib/parse'

export default {
  filters: {
    parseLabel (val) {
      return val || '未登録'
    },
    parseHopeLabel (val) {
      return val || 'こだわらない・未入力'
    }
  },
  props: {
    member: {
      type: Object,
      required: true
    },
    comment: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      itemMap: [
        {
          key: 'educational_background',
          data: () => this.educationalBackground,
          name: '最終学歴'
        }, {
          key: 'graduated_school',
          data: () => this.attribute('graduatedSchool.name'),
          name: '最終学歴（学校名）'
        }, {
          key: 'graduated_school_detail',
          data: () => this.attribute('graduatedSchool.department'),
          name: '最終学歴（学部/学科/専攻）'
        }, {
          key: 'desired_work_prefectures',
          data: () => this.convertSliceArrayLabel(this.member.desiredWorkPrefectures, 1, 3),
          name: 'その他の希望勤務地'
        }, {
          key: 'experienced_occupations',
          data: () => this.convertSliceObjectLabelSuffixYear(this.member.experiencedOccupations, 1, 3),
          name: 'その他の経験職種'
        }, {
          key: 'desired_occupations',
          data: () => this.convertSliceObjectLabel(this.member.desiredOccupations, 1, 3),
          name: 'その他の希望職種'
        }, {
          key: 'experienced_businesses',
          data: () => this.convertSliceArrayLabel(this.member.experiencedBusinesses, 0, 2),
          name: '経験業種'
        }, {
          key: 'desired_businesses',
          data: () => this.convertSliceArrayLabel(this.member.desiredBusinesses, 0, 2),
          name: '希望業種',
          hope: true
        }, {
          key: 'toeic_toefl_score',
          data: () => this.convertToeicAndToefl(this.member.toeicScore, this.member.toeflScore),
          name: 'TOEIC・TOEFL'
        }, {
          key: 'english_skill',
          data: () => this.attribute('englishSkill.name'),
          name: '英語スキル'
        }, {
          key: 'languages_other_than_english',
          data: () => this.attribute('languagesOtherThanEnglish'),
          name: '英語以外の語学スキル'
        }, {
          key: 'company_style',
          data: () => this.attribute('companyStyle.name'),
          name: '希望する会社のタイプ'
        }, {
          key: 'business_experience',
          data: () => this.businessExperience,
          name: '経験業務'
        }, {
          key: 'experience_field',
          data: () => this.experienceField,
          name: '経験分野'
        }, {
          key: 'it_development',
          data: () => this.itDevelopment,
          name: 'IT系スキル'
        }, {
          key: 'electronic',
          data: () => this.electronic,
          name: '電気・電子・機械・メカトロ系スキル'
        }, {
          key: 'creative',
          data: () => this.creative,
          name: 'クリエイティブ関連ソフトウェア'
        }, {
          key: 'company_name',
          data: () => this.latestCompany,
          name: '直近の社名'
        }, {
          key: 'working_period',
          data: () => this.latestCompanyPeriod,
          name: '直近の勤務期間'
        }, {
          key: 'employment_pattern',
          data: () => this.attribute('careerResume.description'),
          name: '直近の職務内容'
        }, {
          key: 'other_company_names',
          data: () => this.attribute('otherCompanies') ? this.attribute('otherCompanies').join('、') : '',
          name: 'その他の社名'
        }, {
          key: 'licenses',
          data: () => this.createLicensesLabel(this.member.otherLicense, this.member.licenses, this.member.skillsNote),
          name: '資格'
        }, {
          key: 'self_pr',
          data: () => this.attribute('publicRelations'),
          name: '自己PR'
        }, {
          key: 'comment',
          data: () => this.comment,
          name: 'コメント'
        }
      ]
    }
  },
  computed: {
    businessExperience () {
      return this.getSkillExperiences('経験業務', 1, 0)
    },
    experienceField () {
      return this.getSkillExperiences('経験分野', 1, 0)
    },
    itDevelopment () {
      return this.getSkillExperiences('開発環境', 1, 2)
    },
    electronic () {
      return this.getSkillExperiences('保有スキル', 1, 2)
    },
    creative () {
      return this.getSkillExperiences('使用ソフトウェア', 1, 2)
    },
    ...mapGetters({
      skillsMaster: 'skillsMaster'
    }),
    ...mapGetters('settings', [
      'scoutDisplayItems'
    ]),
    latestCompany () {
      const publicationCompanyName = this.attribute('careerResume.publicationCompanyName')
      if (_.isBoolean(publicationCompanyName) && !publicationCompanyName) {
        return '****(社名非公開)****'
      }
      const companyName = this.attribute('careerResume.companyName')
      const employmentName = this.attribute('careerResume.employmentPattern.name')
      if (companyName && employmentName) {
        return `${companyName}[${employmentName}]`
      } else if (companyName) {
        return `${companyName}[雇用形態未登録]`
      } else {
        return ''
      }
    },
    latestCompanyPeriod () {
      const firstYear = this.attribute('careerResume.firstYear')
      const lastYear = this.attribute('careerResume.lastYear')
      // 入社年および退社年が未設定＝直近の勤務期間未登録と判断する。
      if (!firstYear && !lastYear) return ''
      // 退社年に9999が設定されている場合は'現在就業中'とする
      return `${this.toLocalDate(firstYear, this.attribute('careerResume.firstMonth'))}〜${lastYear === 9999 ? '現在就業中' : this.toLocalDate(lastYear, this.attribute('careerResume.lastMonth'))}`
    },
    attribute () {
      return (key) => {
        return _.get(this.member, key, '')
      }
    },
    educationalBackground () {
      return this.attribute('educationalBackground.name') + this.parseDate(this.attribute('graduatedSchool.yearMonth'))
    },
    displayItems () {
      return _.map(this.filteredDisplayItems, v => {
        return _.find(this.itemMap, o => o.key === v)
      })
    },
    filteredDisplayItems () {
      return (this.comment) ? this.scoutDisplayItems : _.filter(this.scoutDisplayItems, v => v !== 'comment')
    }
  },
  methods: {
    convertSliceArrayLabel (value, begin, end) {
      if (!value) return ''
      return value.slice(begin, end).map(v => _.get(v, 'name')).join('、')
    },
    convertSliceObjectLabelSuffixYear (value, begin, end) {
      if (!value || !value.length) return ''
      return value.slice(begin, end).map(v => {
        const label = _.get(v, 'name') + ' '
        const yearLabel = v.years !== null ? (v.years === 0 ? '1ヶ月以上' : v.years + '年') : '経験年数未設定'
        return label + yearLabel
      }).join('、')
    },
    createLicensesLabel (otherLicense, licenses, skillsNote) {
      const otherLicenseLabel = otherLicense || ''
      const licenseNamesLabel = (licenses || []).map(license => license.name)
      const skillsNoteLabel = skillsNote || ''
      const label = _.compact([otherLicenseLabel, ...licenseNamesLabel, skillsNoteLabel]).join('、')
      return label.length >= 50 ? label.substr(0, 50) + '...' : label
    },
    convertSliceObjectLabel (value, begin, end) {
      if (!value || !value.length) return ''
      return value.slice(begin, end).map(v => {
        return _.get(v, 'name')
      }).join('、')
    },
    convertToeicAndToefl (toeicScore, toeflScore) {
      if (_.isNumber(toeicScore) || _.isNumber(toeflScore)) {
        return `TOEIC:${parse.numberValue(toeicScore, '点')}、TOEFL:${parse.numberValue(toeflScore, '点')}`
      } else {
        return ''
      }
    },
    getSkillExperiences (filterName, groupLayer, subGroupLayer = groupLayer) {
      if (!this.member.skills) return ''

      const experiences = _.at(_.groupBy(
        _.filter(parseHierarchicalObjects(this.skillsMaster), master => {
          return this.member.skills.some(skill => skill.id === master.id)
        }),
        o => o.path[groupLayer].name
      ), filterName).reduce((arr, it) => (it) ? [...arr, ...it] : arr, [])

      if (experiences.length === 0) {
        return ''
      }
      // 経験年数と補足情報注入
      experiences.map(experience => {
        const experienceSkill = this.member.skills.find(skill => skill.id === experience.id)
        experience.year = experienceSkill.experiencedYears
        experience.note = experienceSkill.note
      })

      const categories = _.groupBy(experiences, experience => {
        return experience.path[subGroupLayer].name
      })

      return Object.keys(categories).map(key => {
        return `[${key}]${categories[key].map(i => {
          return `${i.name}（${i.year}）${i.note ? `:${i.note}` : ''}`
        }).join('、')}`
      }).join(' / ')
    },
    parseDate (yearMonth) {
      return yearMonth ? '(' + createDate(yearMonth, 'YYYY-MM')?.format('YYYY年M月') + ')' : ''
    },
    toLocalDate (year, month) {
      return createDate(`${year}-${month}`, 'YYYY-M')?.format('YYYY年MM月') ?? '未登録'
    }
  }
}
</script>
